import type { FormPlatformType } from "shared/types/post";

type AccountUtmsLike = {
  enabled?: boolean;
  name?: string;
  source?: string;
  medium?: string;
};

export type PlatformAccountLike = {
  platformType?: string | null;
  utms?: AccountUtmsLike;
  igData?: {
    website?: string;
  };
};

const DEFAULT_UTM_CONTENT_ID = "x".repeat(24);

function getUtmSource(platformType: FormPlatformType) {
  switch (platformType) {
    case "LI": {
      return "linkedin";
    }
    case "TH": {
      return "threads";
    }
    default: {
      return "sked";
    }
  }
}

export function getEffectiveUrlWithUtms({
  account,
  redirectUrl,
  source,
  content,
}: {
  account?: PlatformAccountLike;
  redirectUrl?: string | null;
  source: string;
  content: string;
}) {
  const trimmedUrl = redirectUrl?.trim();

  if (!trimmedUrl) {
    return "";
  }

  const utms = account?.utms;

  if (!utms || utms.enabled === false) {
    return trimmedUrl;
  }

  let parsedUrl: URL;

  try {
    parsedUrl = new URL(trimmedUrl);
  } catch {
    return trimmedUrl;
  }

  const hasExistingUtm = [...parsedUrl.searchParams.keys()].some((key) =>
    key.includes("utm_")
  );

  if (hasExistingUtm) {
    return trimmedUrl;
  }

  parsedUrl.searchParams.set("utm_name", utms.name || "sked");
  parsedUrl.searchParams.set("utm_source", utms.source || source || "sked");
  parsedUrl.searchParams.set("utm_medium", utms.medium || "social");

  if (content) {
    parsedUrl.searchParams.set("utm_content", `sked_${content}`);
  }

  return parsedUrl.toString();
}

function getRelevantAccounts(
  selectedAccounts: PlatformAccountLike[],
  selectedPlatform: FormPlatformType
) {
  return selectedAccounts.filter(
    (account) => account.platformType === selectedPlatform
  );
}

export function getUrlUtmStatusMessage({
  url,
  selectedAccounts = [],
  selectedPlatform,
}: {
  url?: string | null;
  selectedAccounts?: PlatformAccountLike[];
  selectedPlatform?: FormPlatformType;
}) {
  const trimmedUrl = url?.trim();

  if (!trimmedUrl) {
    return null;
  }

  if (!selectedPlatform) {
    return null;
  }

  if (!["LI", "TH"].includes(selectedPlatform)) {
    return null;
  }

  let parsedUrl: URL;

  try {
    parsedUrl = new URL(trimmedUrl);
  } catch {
    return null;
  }

  const hasExistingUtm = [...parsedUrl.searchParams.keys()].some((key) =>
    key.includes("utm_")
  );

  if (hasExistingUtm) {
    return "UTM tags won't be added because this URL already includes UTM parameters.";
  }

  const relevantAccounts = getRelevantAccounts(
    selectedAccounts,
    selectedPlatform
  );

  if (relevantAccounts.length === 0) {
    return null;
  }

  const source = getUtmSource(selectedPlatform);
  const enabledCount = relevantAccounts.reduce((count, account) => {
    const effectiveUrl = getEffectiveUrlWithUtms({
      account,
      redirectUrl: trimmedUrl,
      source,
      content: DEFAULT_UTM_CONTENT_ID,
    });

    return effectiveUrl === trimmedUrl ? count : count + 1;
  }, 0);

  if (enabledCount === 0) {
    return relevantAccounts.length === 1
      ? "UTM tags won't be added for this account because they are disabled in account settings."
      : "UTM tags won't be added for the selected accounts because they are disabled in account settings.";
  }

  if (enabledCount === relevantAccounts.length) {
    return relevantAccounts.length === 1
      ? "UTM tags will be added based on this account's settings."
      : "UTM tags will be added for all selected accounts based on each account's settings.";
  }

  return `UTM tags will be added for ${enabledCount} of ${relevantAccounts.length} selected accounts based on each account's settings.`;
}
